import { motion } from 'framer-motion';
import { ArrowRight, Handshake, Percent, Users, Zap, BookOpen, HeadphonesIcon } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';

const partnerTypes = [
  {
    icon: Percent,
    title: 'Affiliate Partners',
    description:
      'Earn commissions by referring customers to SweetPilot. Get 30% recurring commission for the lifetime of referred customers.',
    benefits: [
      '30% recurring commission',
      'Custom referral links',
      'Marketing materials provided',
      'Real-time tracking dashboard',
      'Monthly payouts via PayPal or bank',
    ],
    cta: 'Join Affiliate Program',
  },
  {
    icon: Handshake,
    title: 'Agency Partners',
    description:
      'Offer SweetPilot to your clients as part of your service packages. White-label solutions and volume discounts available.',
    benefits: [
      'White-label dashboard',
      'Volume discounts up to 40%',
      'Priority support channel',
      'Co-marketing opportunities',
      'Early access to new features',
    ],
    cta: 'Become an Agency Partner',
  },
  {
    icon: Zap,
    title: 'Integration Partners',
    description:
      'Build integrations that connect SweetPilot with other platforms. Get listed in our marketplace and earn revenue share.',
    benefits: [
      'API access and documentation',
      'Technical support',
      'Marketplace listing',
      'Revenue share on paid integrations',
      'Joint marketing campaigns',
    ],
    cta: 'Build an Integration',
  },
];

const currentPartners = [
  { name: 'Zapier', type: 'Integration', logo: '⚡' },
  { name: 'Slack', type: 'Integration', logo: '💬' },
  { name: 'Shopify', type: 'Integration', logo: '🛍️' },
  { name: 'HubSpot', type: 'Integration', logo: '🟠' },
  { name: 'Mailchimp', type: 'Integration', logo: '📧' },
  { name: 'Google', type: 'Integration', logo: '🔵' },
  { name: 'Microsoft', type: 'Integration', logo: '🟦' },
  { name: 'Salesforce', type: 'Integration', logo: '☁️' },
];

const testimonials = [
  {
    quote:
      "Partnering with SweetPilot has been a game-changer for our agency. Our clients love the AI employees, and we've increased our revenue by 40%.",
    author: 'Sarah Chen',
    role: 'Founder, Digital Growth Agency',
    avatar: '👩‍💼',
  },
  {
    quote:
      "The affiliate program is incredibly generous. I've earned over $10,000 in the last quarter just by recommending SweetPilot to my audience.",
    author: 'Marcus Johnson',
    role: 'Tech YouTuber',
    avatar: '👨‍💻',
  },
];

export default function Partners() {
  return (
    <StaticPageLayout
      title="Partner Program"
      subtitle="Join our partner ecosystem and grow your business with SweetPilot."
    >
      {/* Partner Types */}
      <section className="mb-16">
        <div className="grid md:grid-cols-3 gap-6">
          {partnerTypes.map((type, index) => (
            <motion.div
              key={type.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-6 rounded-2xl bg-dark-800/50 border border-white/5 hover:border-purple-500/30 transition-all flex flex-col"
            >
              <div className="w-14 h-14 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
                <type.icon className="w-7 h-7 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">{type.title}</h3>
              <p className="text-gray-400 text-sm mb-4">{type.description}</p>
              <ul className="space-y-2 mb-6 flex-1">
                {type.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2 text-sm text-gray-300">
                    <span className="text-green-400 mt-0.5">✓</span>
                    {benefit}
                  </li>
                ))}
              </ul>
              <button className="w-full py-3 rounded-xl bg-purple-500/20 text-purple-400 font-medium hover:bg-purple-500/30 transition-colors flex items-center justify-center gap-2">
                {type.cta} <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Current Partners */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-8 text-center">Our Partners</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {currentPartners.map((partner) => (
            <div
              key={partner.name}
              className="flex items-center gap-3 p-4 rounded-xl bg-dark-800/50 border border-white/5"
            >
              <span className="text-2xl">{partner.logo}</span>
              <div>
                <div className="text-white font-medium">{partner.name}</div>
                <div className="text-gray-500 text-xs">{partner.type}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-8 text-center">Partner Success Stories</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-6 rounded-2xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/5"
            >
              <p className="text-gray-300 text-lg mb-6">"{testimonial.quote}"</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-xl">
                  {testimonial.avatar}
                </div>
                <div>
                  <div className="text-white font-medium">{testimonial.author}</div>
                  <div className="text-gray-400 text-sm">{testimonial.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Partner Resources */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-8">Partner Resources</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: BookOpen,
              title: 'Partner Portal',
              description: 'Access your dashboard, track earnings, and manage referrals.',
            },
            {
              icon: Users,
              title: 'Partner Community',
              description: 'Connect with other partners, share tips, and get exclusive updates.',
            },
            {
              icon: HeadphonesIcon,
              title: 'Partner Support',
              description: 'Dedicated support team for all partner-related questions.',
            },
          ].map((resource, index) => (
            <motion.div
              key={resource.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="flex items-start gap-4 p-6 rounded-2xl bg-dark-800/50 border border-white/5"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0">
                <resource.icon className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">{resource.title}</h3>
                <p className="text-gray-400 text-sm">{resource.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="text-center p-8 rounded-2xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/5">
        <h2 className="text-2xl font-bold text-white mb-4">Ready to Partner with Us?</h2>
        <p className="text-gray-400 mb-6">
          Fill out our partner application and our team will get back to you within 24 hours.
        </p>
        <a
          href="mailto:partners@flowai.com"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white"
        >
          Apply Now <ArrowRight className="w-4 h-4" />
        </a>
      </section>
    </StaticPageLayout>
  );
}
